import { useApiResource, type ApiResource } from "../lib/useApiResource";
import { getConnectorsManifest } from "../lib/connectorsApi";
import type { DashboardProviderConnection } from "../lib/dashboardApi";

/**
 * Loads the lightweight provider manifest (`GET /api/connectors`) — the Import
 * Data modal's fast path. Deliberately NOT `useDashboard`: the modal only needs
 * `connections[]`, so it must not wait on the full dashboard aggregate (heroes,
 * activities, events, …) which made the connect card slow to appear.
 */
export function useConnectorsManifest(): ApiResource<DashboardProviderConnection[]> {
  return useApiResource((signal) => getConnectorsManifest(signal), []);
}

/**
 * The provider offer as the SPA's connect surfaces see it.
 *
 * `offered` is every registered provider in the live manifest (order is the
 * server's), `connected` is the one that's linked — the server allows at most
 * one connection at a time, so this is a single entry or null. Surfaces show a
 * connect card while nothing is connected and a status note once something is.
 */
export function useConnectorOffer(): {
  offered: DashboardProviderConnection[];
  connected: DashboardProviderConnection | null;
  loading: boolean;
} {
  const { data, loading } = useConnectorsManifest();
  const offered = data ?? [];
  return { offered, connected: offered.find((c) => c.connected) ?? null, loading };
}
